import type { ReactNode } from 'react';
import { API } from 'shared/config';

import { Breadcrumbs } from '@/components/Breadcrumbs/index.ts';
import { useStartRefinFlow } from '@/hooks/useStartRefinFlow.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { Button } from '@/ui/button-2.tsx';

import type { ArticleMeta } from '../types.ts';

type ArticleLayoutProps = {
  meta: ArticleMeta;
  hero: ReactNode;
  children: ReactNode;
};

export function ArticleLayout({ meta, hero, children }: ArticleLayoutProps) {
  const startRefinFlow = useStartRefinFlow();

  return (
    <div className="w-full bg-white py-4 pb-16 sm:py-8 xl:py-16">
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={`${API.domain}/${meta.slug}`} />

      <article className="mx-auto flex max-w-6xl flex-col gap-8 px-4">
        <header className="contents">
          <Breadcrumbs
            className="max-md:-mb-4"
            items={[
              { text: 'Hjem', url: '/' },
              { text: 'Din økonomi', url: '/din-okonomi' },
              { text: 'Artikler', url: '/artikler' },
              { text: meta.title },
            ]}
          />

          <h1 className="typography-v1-display">{meta.title}</h1>
          <p className="typography-v1-body1">{meta.description}</p>
          {hero}
        </header>

        <section className="mx-auto flex max-w-3xl flex-col gap-4 typography-v1-body3 [&_h2]:typography-v1-h4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6">
          {children}

          <aside className="contents" aria-label="Sjekk refinansiering">
            <p>
              <strong>Vil du se hvor mye du kan spare?</strong> Sjekk gratis og uforpliktende hva du
              kan få i rente hos Scoopr.
            </p>

            <Button
              color="blue"
              className="my-4 mr-auto"
              onClick={() => {
                sendUserPortalEvent('article_cta_clicked', { article_slug: meta.slug });
                startRefinFlow();
              }}
            >
              Sjekk nå
            </Button>
          </aside>

          <footer className="italic">
            Denne artikkelen er generell informasjon og ikke personlig økonomisk rådgivning. Står du
            i en vanskelig økonomisk situasjon, kan gjeldsrådgivningstjenesten i din kommune eller
            NAV gi gratis og uavhengig veiledning.
          </footer>
        </section>
      </article>
    </div>
  );
}
